import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetList } from "react-admin";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  Button,
  Stack,
  Paper,
} from "@mui/material";
import { Search as SearchIcon, ArrowBack, ChevronRight, RocketLaunch, MenuBook } from "@mui/icons-material";
import { ResourceName } from "@/types";
import { getResourceIcon } from "@/utils/icons";
import { getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { toAbsoluteImageUrls } from "@/resources/help/imageUrls";
import { sanitizeArticleHtml } from "@/utils/sanitizeHtml";

// User-facing Knowledge Base (Feature 4317 / N4). Hero search + browse-by-category
// card grid, modeled on standard help centers. Published articles only (the API
// enforces published-only for non-editors); list records carry body_html, so
// opening an article needs no extra fetch.

interface HelpArticle {
  id: string;
  slug: string;
  title: string;
  body_html: string;
  category_id?: string;
  status: string;
}

interface HelpCategory {
  id: string;
  slug: string;
  name: string;
  description?: string;
  sort_order: number;
}

// Map record-type category slugs to their resource so cards reuse the exact
// icons used across the app (Clients, Policies, Claimants, etc.). Categories
// without a matching record type fall back to a generic icon.
const CATEGORY_RESOURCE_BY_SLUG: Record<string, ResourceName> = {
  clients: ResourceName.CLIENTS,
  policies: ResourceName.POLICIES,
  claimants: ResourceName.CLAIMANTS,
  "stop-loss-claims": ResourceName.STOPLOSS_CLAIMS,
  reimbursements: ResourceName.REIMBURSEMENTS,
  tasks: ResourceName.TASKS,
  documents: ResourceName.DOCUMENTS,
  vendors: ResourceName.VENDORS,
};

const categoryIcon = (slug: string) => {
  const resource = CATEGORY_RESOURCE_BY_SLUG[slug];
  if (resource) return getResourceIcon(resource);
  if (slug === "getting-started") return RocketLaunch;
  return MenuBook;
};

const stripHtml = (html?: string) =>
  (html ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const HelpCenter = () => {
  const [q, setQ] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);

  const { data: categories = [], isLoading: catsLoading } = useGetList<HelpCategory>(ResourceName.HELP_CATEGORIES, {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "sort_order", order: "ASC" },
  });

  // All published articles once — powers per-category counts, category browse, and search.
  const { data: articles = [], isLoading: artsLoading } = useGetList<HelpArticle>(ResourceName.HELP_ARTICLES, {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "title", order: "ASC" },
  });

  const countByCategory = useMemo(() => {
    const m: Record<string, number> = {};
    articles.forEach((a) => {
      if (a.category_id) m[a.category_id] = (m[a.category_id] || 0) + 1;
    });
    return m;
  }, [articles]);

  const searching = q.trim().length > 0;
  const searchResults = useMemo(() => {
    if (!searching) return [];
    const needle = q.trim().toLowerCase();
    return articles.filter((a) => `${a.title} ${stripHtml(a.body_html)}`.toLowerCase().includes(needle));
  }, [q, searching, articles]);

  const categoryArticles = useMemo(
    () => (selectedCategory ? articles.filter((a) => a.category_id === selectedCategory.id) : []),
    [selectedCategory, articles]
  );

  // Contextual deep link: /help?article=<slug> opens that article directly (used
  // by the in-app "Help?" affordances, N5). Runs once after articles load; an
  // unknown slug warns and falls back to the Help Center home.
  const [searchParams] = useSearchParams();
  const articleSlug = searchParams.get("article");
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || !articleSlug || articles.length === 0) return;
    autoOpened.current = true;
    const found = articles.find((a) => a.slug === articleSlug);
    if (found) setSelectedArticle(found);
    else console.warn(`[HelpCenter] No published article for slug "${articleSlug}"`);
  }, [articleSlug, articles]);

  const resetToHome = () => {
    setSelectedArticle(null);
    setSelectedCategory(null);
    setQ("");
  };

  // A single bordered card of article rows (title + snippet + chevron), used by
  // category browse and search results — matches the help-center collection look.
  const ArticleRows = ({ items }: { items: HelpArticle[] }) => (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      {items.map((a, idx) => (
        <Box key={a.id}>
          {idx > 0 && <Divider />}
          <Box
            onClick={() => setSelectedArticle(a)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 2,
              cursor: "pointer",
              transition: "background-color 120ms",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {a.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {stripHtml(a.body_html)}
              </Typography>
            </Box>
            <ChevronRight sx={{ color: "text.disabled", flexShrink: 0 }} />
          </Box>
        </Box>
      ))}
    </Paper>
  );

  const CategoryIcon = selectedCategory ? categoryIcon(selectedCategory.slug) : MenuBook;

  return (
    <Box sx={{ pb: 6 }}>
      {/* Hero */}
      <Box
        sx={(theme) => ({
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: theme.palette.primary.contrastText,
          py: { xs: 5, md: 8 },
          px: 2,
          textAlign: "center",
        })}
      >
        <Typography variant="h3" fontWeight={700} gutterBottom>
          How can we help?
        </Typography>
        <Box sx={{ maxWidth: 720, mx: "auto", mt: 3 }}>
          <TextField
            fullWidth
            placeholder="Search for articles…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSelectedArticle(null);
              setSelectedCategory(null);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: {
                bgcolor: "background.paper",
                borderRadius: 2,
                // Stay solid white — no bg/border color shift on hover or focus.
                "&:hover": { bgcolor: "background.paper" },
                "&.Mui-focused": { bgcolor: "background.paper" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1140, mx: "auto", px: 3, mt: 4 }}>
        {selectedArticle ? (
          // ── Article detail ──
          <Card>
            <CardContent>
              <Button startIcon={<ArrowBack />} size="small" onClick={() => setSelectedArticle(null)} sx={{ mb: 2 }}>
                Back
              </Button>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {selectedArticle.title}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{ "& img": { maxWidth: "100%" }, "& h2,& h3": { mt: 2 } }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeArticleHtml(toAbsoluteImageUrls(selectedArticle.body_html, getPartnerAwareApiUrl())),
                }}
              />
            </CardContent>
          </Card>
        ) : artsLoading || catsLoading ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : searching ? (
          // ── Search results ──
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {searchResults.length} result{searchResults.length === 1 ? "" : "s"} for “{q.trim()}”
            </Typography>
            {searchResults.length === 0 ? (
              <Typography color="text.secondary">No articles found.</Typography>
            ) : (
              <ArticleRows items={searchResults} />
            )}
          </>
        ) : selectedCategory ? (
          // ── Category browse ──
          <>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={(theme) => ({
                  width: 52,
                  height: 52,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: theme.palette.action.hover,
                  color: theme.palette.primary.main,
                  flexShrink: 0,
                })}
              >
                <CategoryIcon />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  {selectedCategory.name}
                </Typography>
                {selectedCategory.description && (
                  <Typography color="text.secondary">{selectedCategory.description}</Typography>
                )}
              </Box>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              {categoryArticles.length} article{categoryArticles.length === 1 ? "" : "s"}
            </Typography>
            {categoryArticles.length === 0 ? (
              <Typography color="text.secondary">No published articles in this category yet.</Typography>
            ) : (
              <ArticleRows items={categoryArticles} />
            )}
          </>
        ) : (
          // ── Home: browse by category ──
          <>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3, textAlign: "center" }}>
              Browse by category
            </Typography>
            {categories.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                No help categories yet.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gap: 2.5,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                }}
              >
                {categories.map((c) => {
                  const Icon = categoryIcon(c.slug);
                  const count = countByCategory[c.id] || 0;
                  return (
                    <Card key={c.id} variant="outlined" sx={{ height: "100%", "&:hover": { boxShadow: 4 } }}>
                      <CardActionArea sx={{ height: "100%" }} onClick={() => setSelectedCategory(c)}>
                        <CardContent>
                          <Box
                            sx={(theme) => ({
                              width: 44,
                              height: 44,
                              borderRadius: 1.5,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: theme.palette.action.hover,
                              color: theme.palette.primary.main,
                              mb: 1.5,
                            })}
                          >
                            <Icon />
                          </Box>
                          <Typography variant="h6" fontWeight={600}>
                            {c.name}
                          </Typography>
                          {c.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, minHeight: 40 }}>
                              {c.description}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
                            {count} article{count === 1 ? "" : "s"}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {!selectedArticle && (searching || selectedCategory) && (
          <Button onClick={resetToHome} sx={{ mt: 3 }} size="small">
            ← Back to Help Center home
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default HelpCenter;
